// src/pages/admin/library/BookManagement.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  FaSearch, 
  FaPlus, 
  FaEdit,
  FaTrash,
  FaEye,
  FaSync,
  FaFilter,
  FaBook,
  FaImage,
  FaPrint,
  FaFileImport
} from '../../../utils/iconUtils';
import { useUI } from '../../../components/common/UIMessageProvider';
import AddBookModal from './AddBookModal';
import ViewBookModal from './ViewBookModal';
import { 
  LibraryBook, 
  LibraryCategory,
  getLibraryBooks, 
  getLibraryCategories,
  createLibraryBook,
  updateLibraryBook,
  deleteLibraryBook,
  uploadBookCover,
  deleteBookCover,
  invalidateDashboardCaches
} from '../../../firebase/libraryFirestoreService';

interface BookWithCategory extends LibraryBook {
  category?: LibraryCategory;
}

// Cache configuration
const BOOKS_CACHE_KEY = 'library_books_cache';
const BOOKS_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
const BOOK_CATEGORIES_CACHE_KEY = 'book_categories_cache';
const BOOK_CATEGORIES_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
const CACHE_VERSION = '1.1.0'; // Version for schema changes
const BACKGROUND_MIN_AGE = 30 * 60 * 1000; // 30 minutes - minimum age before background refresh

// Type guard for cached data
const isValidBookData = (data: any): data is BookWithCategory[] => {
  return Array.isArray(data) && data.every(item => 
    item && 
    typeof item === 'object' &&
    typeof item.title === 'string' &&
    typeof item.isbn === 'string'
  );
};

const isValidCategoryData = (data: any): data is LibraryCategory[] => {
  return Array.isArray(data) && data.every(item => 
    item && 
    typeof item === 'object' &&
    typeof item.name === 'string'
  );
};

// Helper for debouncing
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

// Helper for CSV escaping
const escapeCSVField = (field: any): string => {
  if (field === null || field === undefined || field === '') {
    return '';
  }
  
  const str = String(field);
  // Escape quotes and wrap in quotes if contains comma, newline, or quotes
  if (str.includes(',') || str.includes('\n') || str.includes('"') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  
  return str;
};

const BookManagement: React.FC = () => {
  const ui = useUI();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [books, setBooks] = useState<BookWithCategory[]>([]);
  const [filteredBooks, setFilteredBooks] = useState<BookWithCategory[]>([]);
  const [allBooks, setAllBooks] = useState<BookWithCategory[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBook, setEditingBook] = useState<BookWithCategory | null>(null);
  const [viewBook, setViewBook] = useState<BookWithCategory | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // Abort controller for fetch cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cache functions with error handling and versioning
  const isCacheValid = useCallback((cacheKey: string, expiry: number): boolean => {
    try {
      const cacheData = localStorage.getItem(cacheKey);
      if (!cacheData) return false;
      
      const { timestamp, version } = JSON.parse(cacheData);
      const age = Date.now() - timestamp;
      return age < expiry && version === CACHE_VERSION;
    } catch (error) {
      console.warn('Cache validation error:', error);
      return false;
    }
  }, []);

  const getCachedData = useCallback(<T,>(cacheKey: string, validator: (data: any) => data is T[]): T[] => {
    try {
      const cacheData = localStorage.getItem(cacheKey);
      if (!cacheData) return [];

      const { data } = JSON.parse(cacheData);
      return validator(data) ? data : [];
    } catch (error) {
      console.warn('Cache read error:', error);
      return [];
    }
  }, []);

  const saveToCache = useCallback(<T,>(cacheKey: string, data: T[]): boolean => {
    try {
      // Check size before saving
      const dataStr = JSON.stringify(data);
      if (dataStr.length > 2 * 1024 * 1024) { // 2MB warning
        console.warn(`Cache data for ${cacheKey} is large: ${(dataStr.length / 1024 / 1024).toFixed(2)}MB`);
        // Store only essential fields for large datasets
        if (cacheKey === BOOKS_CACHE_KEY) {
          const essentialData = (data as any[]).map(book => ({
            id: (book as any).id,
            isbn: (book as any).isbn,
            title: (book as any).title,
            author: (book as any).author,
            categoryId: (book as any).categoryId,
            totalCopies: (book as any).totalCopies,
            availableCopies: (book as any).availableCopies,
            // Omit imageUrl to reduce size
          }));
          const cacheData = {
            timestamp: Date.now(),
            data: essentialData,
            version: CACHE_VERSION,
            isEssential: true // Flag to indicate reduced dataset
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          return true;
        }
      }
      
      const cacheData = {
        timestamp: Date.now(),
        data,
        version: CACHE_VERSION
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      return true;
    } catch (error: any) {
      console.error('Failed to save cache:', error);
      if (error.name === 'QuotaExceededError') {
        clearOldestCaches();
        // Try again
        try {
          const cacheData = {
            timestamp: Date.now(),
            data,
            version: CACHE_VERSION
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          return true;
        } catch (retryError) {
          console.error('Failed retry:', retryError);
        }
      }
      return false;
    }
  }, []);

  const clearOldestCaches = () => {
    try {
      const cacheKeys: { key: string; timestamp: number }[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('_cache') || key.includes('dashboard'))) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const parsed = JSON.parse(data);
              cacheKeys.push({ key, timestamp: parsed.timestamp || 0 });
            }
          } catch (e) {
            // Ignore corrupted items
          }
        }
      }
      
      // Sort by oldest first
      cacheKeys.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 25%
      const toRemove = Math.ceil(cacheKeys.length * 0.25);
      for (let i = 0; i < toRemove && i < cacheKeys.length; i++) {
        localStorage.removeItem(cacheKeys[i].key);
      }
    } catch (error) {
      console.error('Error clearing old caches:', error);
    }
  };

  const invalidateCache = useCallback((cacheKey: string) => {
    try {
      localStorage.removeItem(cacheKey);
      return true;
    } catch (error) {
      console.warn('Failed to invalidate cache:', error);
      return false;
    }
  }, []);

  // Apply filters to cached data
  const applyFiltersToCachedData = useCallback(() => {
    let filtered = [...allBooks];
    
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(book =>
        book.title?.toLowerCase().includes(term) ||
        book.author?.toLowerCase().includes(term) ||
        book.isbn?.toLowerCase().includes(term) ||
        book.category?.name?.toLowerCase().includes(term)
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(book => book.category?.id === categoryFilter);
    }
    
    if (statusFilter !== 'all') {
      if (statusFilter === 'in-stock') {
        filtered = filtered.filter(book => (book.availableCopies || 0) > 0);
      } else if (statusFilter === 'out-of-stock') {
        filtered = filtered.filter(book => (book.availableCopies || 0) === 0);
      } else if (statusFilter === 'low-stock') {
        filtered = filtered.filter(book => {
          const available = book.availableCopies || 0;
          const total = book.totalCopies || 0;
          return available > 0 && available <= 3;
        });
      }
    }
  
    setFilteredBooks(filtered);
    setCurrentPage(1);
  }, [allBooks, categoryFilter, statusFilter, debouncedSearchTerm]);

  // Calculate available copies when total copies change
  const calculateAvailableCopies = useCallback((
    oldTotalCopies: number,
    oldAvailableCopies: number,
    newTotalCopies: number
  ): number => {
    if (newTotalCopies === oldTotalCopies) {
      return oldAvailableCopies;
    }
    
    if (newTotalCopies > oldTotalCopies) {
      // Increasing total copies - add difference to available
      const difference = newTotalCopies - oldTotalCopies;
      return oldAvailableCopies + difference;
    } else {
      // Decreasing total copies - reduce available proportionally, but not below 0
      const difference = oldTotalCopies - newTotalCopies;
      const newAvailable = Math.max(0, oldAvailableCopies - difference);
      return Math.min(newAvailable, newTotalCopies);
    }
  }, []);

  const fetchData = useCallback(async (isBackgroundRefresh = false) => {
    // Cancel previous fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    if (isBackgroundRefresh) {
      setBackgroundRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      // Try to get books and categories from cache first
      const shouldUseBooksCache = isCacheValid(BOOKS_CACHE_KEY, BOOKS_CACHE_EXPIRY);
      const shouldUseCategoriesCache = isCacheValid(BOOK_CATEGORIES_CACHE_KEY, BOOK_CATEGORIES_CACHE_EXPIRY);
      
      let booksData: BookWithCategory[] = [];
      let categoriesData: LibraryCategory[] = [];
      
      // Fetch books (from cache or Firebase)
      if (!isBackgroundRefresh && shouldUseBooksCache) {
        booksData = getCachedData<BookWithCategory>(BOOKS_CACHE_KEY, isValidBookData);
        if (booksData.length > 0) {
          console.log('📚 Using cached books');
        }
      }
      
      if (booksData.length === 0 || isBackgroundRefresh) {
        const booksResult = await getLibraryBooks();
        if (booksResult.success && !abortController.signal.aborted) {
          booksData = booksResult.data as BookWithCategory[];
          saveToCache(BOOKS_CACHE_KEY, booksData);
          if (isBackgroundRefresh) {
            console.log('🔄 Background refresh completed');
          } else {
            console.log('🔄 Fetched books from Firebase and cached');
          }
        } else if (!isBackgroundRefresh && booksResult.error) {
          console.error('Failed to fetch books:', booksResult.error);
          // Try stale cache
          const staleCache = getCachedData<BookWithCategory>(BOOKS_CACHE_KEY, isValidBookData);
          if (staleCache.length > 0) {
            booksData = staleCache;
            ui.showMessage('Using cached book data (offline mode)', { type: 'info' });
          }
        }
      }
      
      // Fetch categories (from cache or Firebase)
      if (!isBackgroundRefresh && shouldUseCategoriesCache) {
        categoriesData = getCachedData<LibraryCategory>(BOOK_CATEGORIES_CACHE_KEY, isValidCategoryData);
      }
      
      if (categoriesData.length === 0 || isBackgroundRefresh) {
        const categoriesResult = await getLibraryCategories();
        if (categoriesResult.success && !abortController.signal.aborted) {
          categoriesData = categoriesResult.data;
          saveToCache(BOOK_CATEGORIES_CACHE_KEY, categoriesData);
        } else if (!isBackgroundRefresh && categoriesResult.error) {
          console.error('Failed to fetch categories:', categoriesResult.error);
          // Try stale cache
          const staleCache = getCachedData<LibraryCategory>(BOOK_CATEGORIES_CACHE_KEY, isValidCategoryData);
          if (staleCache.length > 0) {
            categoriesData = staleCache;
          }
        }
      }
      
      // Map categories to books
      const booksWithCategories: BookWithCategory[] = booksData.map(book => {
        const category = categoriesData.find(cat => cat.id === book.categoryId);
        return { ...book, category };
      });
      
      if (!abortController.signal.aborted) {
        setAllBooks(booksWithCategories);
        setBooks(booksWithCategories);
        setCategories(categoriesData);
        setFilteredBooks(booksWithCategories);
      }
      
    } catch (error) {
      if (!isBackgroundRefresh) {
        console.error('Error fetching data:', error);
        if (!abortController.signal.aborted) {
          ui.showMessage('Error loading books', { type: 'error' });
        }
      }
    } finally {
      if (!abortController.signal.aborted) {
        if (isBackgroundRefresh) {
          setBackgroundRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [isCacheValid, getCachedData, saveToCache, ui]);

  // Background refresh
  const backgroundRefresh = useCallback(async () => {
    if (backgroundRefreshing) return;
    
    // Only refresh if cache is older than 30 seconds
    if (isCacheValid(BOOKS_CACHE_KEY, BOOKS_CACHE_EXPIRY)) {
      const cacheData = localStorage.getItem(BOOKS_CACHE_KEY);
      if (cacheData) {
        const { timestamp } = JSON.parse(cacheData);
        if (Date.now() - timestamp < BACKGROUND_MIN_AGE) {
          return; // Cache is fresh enough
        }
      }
    }
    
    await fetchData(true);
    applyFiltersToCachedData();
  }, [backgroundRefreshing, isCacheValid, fetchData, applyFiltersToCachedData]);

  // Handle storage events for multi-tab sync
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === BOOKS_CACHE_KEY) {
        console.log('🔄 Storage event detected, refreshing from cache');
        const cachedBooks = getCachedData<BookWithCategory>(BOOKS_CACHE_KEY, isValidBookData);
        setAllBooks(cachedBooks);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [getCachedData]);

  // Background refresh on tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        backgroundRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Periodic refresh every 5 minutes
    const interval = setInterval(() => {
      if (document.hasFocus()) {
        backgroundRefresh();
      }
    }, BACKGROUND_MIN_AGE);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
      // Clean up abort controller
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [backgroundRefresh]);

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchData(false);
  }, [fetchData]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFiltersToCachedData();
  }, [applyFiltersToCachedData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is handled by debounced term
  };

  const handleRefresh = () => {
    // Force refresh by invalidating cache
    invalidateCache(BOOKS_CACHE_KEY);
    invalidateCache(BOOK_CATEGORIES_CACHE_KEY);
    fetchData(false);
  };

  const handleDeleteBook = async (bookId: string, bookTitle: string) => {
    // Check if book has copies
    const book = allBooks.find(b => b.id === bookId);
    if (book && ((book.totalCopies || 0) > 0 || (book.availableCopies || 0) > 0)) {
      ui.showMessage(
        `Cannot delete "${bookTitle}" because it has ${book.totalCopies || 0} copies. ` +
        'Please remove all copies first.',
        { type: 'warning' }
      );
      return;
    }

    const ok = await ui.askConfirm({ 
      title: 'Delete Book', 
      message: `Are you sure you want to delete "${bookTitle}"? This action cannot be undone.` 
    });
    if (!ok) return;

    try {
      setLoading(true);
      const result = await deleteLibraryBook(bookId);
      if (result.success) {
        ui.showMessage('Book deleted successfully!');
        // Invalidate cache and refetch
        invalidateCache(BOOKS_CACHE_KEY);
        try {
          invalidateDashboardCaches();
        } catch (e) {
          console.warn('Failed to invalidate dashboard caches', e);
        }
        fetchData(false);
      } else {
        ui.showMessage(`Error: ${result.error}`, { type: 'error' });
      }
    } catch (error) {
      ui.showMessage('Error deleting book', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddBook = () => {
    setEditingBook(null);
    setShowAddModal(true);
  };

  const handleEditClick = (book: BookWithCategory) => {
    setEditingBook(book);
    setShowAddModal(true);
  };

  const handleViewBook = (book: BookWithCategory) => {
    setViewBook(book);
    setShowViewModal(true);
  };

  const handleSaveBook = async (bookData: any) => {
    setLoading(true);
    try {
      // Calculate available copies for updates
      let availableCopies = bookData.totalCopies; // Default for new books
      
      if (editingBook) {
        const oldTotalCopies = editingBook.totalCopies || 0;
        const oldAvailableCopies = editingBook.availableCopies || 0;
        const newTotalCopies = bookData.totalCopies;
        
        availableCopies = calculateAvailableCopies(
          oldTotalCopies,
          oldAvailableCopies,
          newTotalCopies
        );
      }

      // Prepare book payload
      const bookPayload: Partial<LibraryBook> = {
        isbn: bookData.isbn.trim(),
        title: bookData.title.trim(),
        author: bookData.author.trim(),
        categoryId: bookData.categoryId,
        totalCopies: bookData.totalCopies,
        availableCopies,
        searchTerms: [
          bookData.isbn.toLowerCase(),
          bookData.title.toLowerCase(),
          bookData.author.toLowerCase(),
          ...(bookData.description?.toLowerCase().split(' ') || [])
        ].filter(term => term)
      };

      // Handle image upload
      if (bookData.imageFile) {
        const uploadResult = await uploadBookCover(
          bookData.imageFile,
          bookData.isbn,
          editingBook?.imageUrl
        );
        
        if (uploadResult.success && uploadResult.url) {
          bookPayload.imageUrl = uploadResult.url;
        }
      } else if (bookData.removeImage) {
        if (editingBook?.imageUrl) {
          await deleteBookCover(editingBook.imageUrl);
        }
        bookPayload.imageUrl = '';
        // Clear image error state
        if (editingBook?.id) {
          setImageErrors(prev => ({ ...prev, [editingBook.id!]: false }));
        }
      } else if (editingBook && bookData.imageUrl) {
        // Keep existing image
        bookPayload.imageUrl = bookData.imageUrl;
      }

      if (editingBook && editingBook.id) {
        // Update existing book
        const res = await updateLibraryBook(editingBook.id, bookPayload);
        if (!res.success) throw new Error(res.error || 'Update failed');
        ui.showMessage('Book updated successfully!');
      } else {
        // Create new book
        const res = await createLibraryBook(bookPayload as LibraryBook);
        if (!res.success) throw new Error(res.error || 'Create failed');
        ui.showMessage('Book added successfully!');
      }

      // Invalidate cache and refetch
      invalidateCache(BOOKS_CACHE_KEY);
      try {
        invalidateDashboardCaches();
      } catch (e) {
        console.warn('Failed to invalidate dashboard caches', e);
      }
      await fetchData(false);
      setShowAddModal(false);
      setEditingBook(null);
    } catch (error: any) {
      console.error('Save error:', error);
      ui.showMessage(`Error saving book: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = () => {
    // Create file input for CSV
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const csvData = event.target?.result as string;
        // TODO: Implement CSV parsing and batch import
        ui.showMessage('CSV import feature coming soon', { type: 'info' });
      };
      reader.readAsText(file);
    };
    
    input.click();
  };

  const handleExportCSV = () => {
    const headers = ['ISBN', 'Title', 'Author', 'Category', 'Total Copies', 'Available Copies', 'Status'];
    const csvContent = [
      headers.map(escapeCSVField).join(','),
      ...filteredBooks.map(book => [
        book.isbn,
        book.title,
        book.author,
        book.category?.name || 'Uncategorized',
        book.totalCopies || 0,
        book.availableCopies || 0,
        (book.availableCopies || 0) > 0 ? 'In Stock' : 'Out of Stock'
      ].map(escapeCSVField).join(','))
    ].join('\r\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `books_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImageError = (bookId: string) => {
    setImageErrors(prev => ({ ...prev, [bookId]: true }));
  };

  // Cache status indicator
  const cacheStatus = useMemo(() => {
    if (loading) return 'Loading...';
    if (backgroundRefreshing) return '🔄 Background refreshing...';
    if (isCacheValid(BOOKS_CACHE_KEY, BOOKS_CACHE_EXPIRY)) return '📚 Using cached data';
    return '🔄 Online data';
  }, [loading, backgroundRefreshing, isCacheValid]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalBooks = allBooks.length;
    const totalCopies = allBooks.reduce((sum, book) => sum + (book.totalCopies || 0), 0);
    const availableCopies = allBooks.reduce((sum, book) => sum + (book.availableCopies || 0), 0);
    const outOfStock = allBooks.filter(book => (book.availableCopies || 0) === 0).length;
    const lowStock = allBooks.filter(book => {
      const available = book.availableCopies || 0;
      return available > 0 && available <= 3;
    }).length;
    
    return {
      totalBooks,
      totalCopies,
      availableCopies,
      outOfStock,
      lowStock
    };
  }, [allBooks]);

  // Pagination helpers
  const totalPages = Math.max(1, Math.ceil(filteredBooks.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedBooks = filteredBooks.slice(startIndex, endIndex);

  const getStockBadge = (available: number, total: number) => {
    if (available === 0) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Out of Stock
        </span>
      );
    } else if (available < 3) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          Low Stock ({available})
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          In Stock ({available})
        </span>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Modals */}
      {showAddModal && (
        <AddBookModal
          isOpen={showAddModal}
          initialData={editingBook ? {
            isbn: editingBook.isbn || '',
            title: editingBook.title || '',
            author: editingBook.author || '',
            categoryId: editingBook.categoryId || '',
            totalCopies: editingBook.totalCopies || 1,
            imageUrl: editingBook.imageUrl || ''
          } : undefined}
          onClose={() => { setShowAddModal(false); setEditingBook(null); }}
          onSave={handleSaveBook}
          loading={loading}
          categories={categories}
        />
      )}

      {showViewModal && (
        <ViewBookModal
          isOpen={showViewModal}
          book={viewBook}
          onClose={() => { setShowViewModal(false); setViewBook(null); }}
        />
      )}

      {/* Statistics Banner */}
      {/* <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-700">{statistics.totalBooks}</div>
            <div className="text-xs text-blue-600">Total Titles</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-700">{statistics.totalCopies}</div>
            <div className="text-xs text-green-600">Total Copies</div>
          </div>
     
        </div>
      </div> */}

      {/* Top Controls */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Left: Filters & Cache Status */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Cache Status */}
            <div className="text-xs text-gray-500 hidden sm:block">
              {cacheStatus} {backgroundRefreshing && '(background)'}
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <FaFilter className="h-3 w-3 text-gray-500" />
              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="in-stock">In Stock</option>
              <option value="out-of-stock">Out of Stock</option>
              <option value="low-stock">Low Stock</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-md">
            <form onSubmit={handleSearch} className="flex items-center">
              <div className="relative flex-1">
                <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
                <input
                  type="text"
                  placeholder="Search by title, author, ISBN, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-sm pl-8 pr-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
                />
              </div>
            </form>
          </div>

          {/* Right: Action Buttons */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Export CSV */}
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title="Export to CSV"
            >
              <FaFileImport className="h-3 w-3 transform rotate-180" />
              <span className="hidden sm:inline">Export</span>
            </button>

            {/* Import CSV */}
            <button
              onClick={handleImportCSV}
              className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title="Import from CSV"
            >
              <FaFileImport className="h-3 w-3" />
              <span className="hidden sm:inline">Import</span>
            </button>

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={backgroundRefreshing}
              className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh from Firebase"
            >
              <FaSync className={`h-3 w-3 ${backgroundRefreshing ? 'animate-spin' : 'text-gray-600'}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Print */}
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title="Print"
            >
              <FaPrint className="h-3 w-3" />
              <span className="hidden sm:inline">Print</span>
            </button>

            {/* Add New Book */}
            <button
              onClick={handleAddBook}
              className="flex items-center gap-2 text-sm px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              title="Add New Book"
            >
              <FaPlus className="h-3 w-3" />
              <span className="hidden sm:inline">Add Book</span>
            </button>
          </div>
        </div>

        {/* Cache Info */}
        <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded">
          <div className="flex items-center justify-between">
            <span>
              <strong>Cache Info:</strong> {allBooks.length} books cached • 
              Last refresh: {(() => {
                try {
                  const cacheData = localStorage.getItem(BOOKS_CACHE_KEY);
                  if (cacheData) {
                    const { timestamp } = JSON.parse(cacheData);
                    const minutesAgo = Math.floor((Date.now() - timestamp) / (60 * 1000));
                    return minutesAgo === 0 ? 'just now' : `${minutesAgo} minutes ago`;
                  }
                } catch (e) {}
                return 'unknown';
              })()}
            </span>
            <span className="text-xs px-2 py-0.5 bg-gray-200 rounded">
              v{CACHE_VERSION}
            </span>
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading && !backgroundRefreshing ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading books...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs leading-tight">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Cover</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Book Details</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    {/* <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Stock</th> */}
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedBooks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-gray-500 text-sm">
                        {allBooks.length === 0
                          ? 'No books found. Add your first book!'
                          : 'No books match your filters.'}
                      </td>
                    </tr>
                  ) : (
                    displayedBooks.map((book, index) => (
                      <tr key={book.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 font-medium">
                          {startIndex + index + 1}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="w-12 h-16 bg-white rounded border border-gray-300 overflow-hidden flex items-center justify-center">
                            {book.imageUrl && !imageErrors[book.id!] ? (
                              <img
                                src={book.imageUrl}
                                alt={book.title}
                                className="max-w-full max-h-full object-contain"
                                onError={() => handleImageError(book.id!)}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                <FaBook className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-gray-900 line-clamp-1">
                              {book.title}
                            </div>
                            <div className="text-xs text-gray-600">
                              by {book.author}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              ISBN: {book.isbn}
                            </div>
                            <div className="text-xs text-gray-500">
                              Total: {book.totalCopies ?? 0} 
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {book.category ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: book.category.color || '#3b82f6' }}
                              />
                              <span className="text-xs text-gray-700">
                                {book.category.name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No category</span>
                          )}
                        </td>
                        {/* <td className="px-3 py-2 whitespace-nowrap">
                          {getStockBadge(book.availableCopies || 0, book.totalCopies || 0)}
                        </td> */}
                      
                        <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewBook(book)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View"
                            >
                              <FaEye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditClick(book)}
                              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                              title="Edit"
                            >
                              <FaEdit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => book.id && handleDeleteBook(book.id, book.title)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete"
                              disabled={(book.totalCopies || 0) > 0 || (book.availableCopies || 0) > 0}
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredBooks.length > itemsPerPage && (
              <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-700">
                  <div>
                    Showing <span className="font-medium">{filteredBooks.length === 0 ? 0 : startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredBooks.length)}</span> of{' '}
                    <span className="font-medium">{filteredBooks.length}</span> books
                    <span className="ml-2 text-gray-500">(Total in cache: {allBooks.length})</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                        currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }

                      if (pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-2 py-1 rounded text-xs ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="px-2">...</span>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                            currentPage === totalPages ? 'bg-primary-600 text-white' : ''
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                        currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookManagement;
